/**
 * WhatsNew — the in-app release notes page (/whats-new).
 *
 * Reverse-chronological, one card per user-facing change, grouped by release. The copy is
 * deliberately functional: what someone can now do, or what stopped happening to them. No
 * commit messages, no internal object names — those live in git and nobody outside engineering
 * can act on them.
 *
 * Data: `release_notes` (migration 2026-09-24-009). Reading is restricted in the database to
 * active internal profiles, and only PUBLISHED notes reach a reader. The route's role list
 * mirrors that allowlist, so nobody who can read the data is shown a page that then refuses it,
 * and nobody who cannot is shown a page that renders empty.
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getPublishedReleaseNotes,
  groupReleaseNotes,
} from "../services/releaseNotes.service";
import {
  RELEASE_NOTE_AUDIENCE_LABELS,
  RELEASE_NOTE_CATEGORY_LABELS,
  type ReleaseNote,
} from "../types/releaseNote.types";
import { formatDate } from "../utils/formatters";

/**
 * Category colours. An unknown category (a migration ahead of this deploy) falls through to a
 * neutral grey rather than rendering an unstyled badge or throwing.
 */
const CATEGORY_STYLES: Record<string, string> = {
  events: "bg-blue-50 text-blue-700 ring-blue-200",
  contacts: "bg-purple-50 text-purple-700 ring-purple-200",
  support: "bg-amber-50 text-amber-700 ring-amber-200",
  campaigns: "bg-pink-50 text-pink-700 ring-pink-200",
  privacy: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  platform: "bg-slate-50 text-slate-700 ring-slate-200",
};
const NEUTRAL_STYLE = "bg-gray-50 text-gray-700 ring-gray-200";

/** "support_team" -> "Support team", for a value the labels map has not caught up with yet. */
function humanise(value: string): string {
  const spaced = value.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const Badge: React.FC<{ className: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}
  >
    {children}
  </span>
);

const NoteCard: React.FC<{ note: ReleaseNote }> = ({ note }) => {
  const categoryLabel =
    RELEASE_NOTE_CATEGORY_LABELS[note.category] ?? humanise(note.category);
  const audienceLabel =
    RELEASE_NOTE_AUDIENCE_LABELS[note.audience] ?? humanise(note.audience);

  return (
    <article
      id={note.slug}
      data-testid="release-note"
      data-slug={note.slug}
      className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Badge className={CATEGORY_STYLES[note.category] ?? NEUTRAL_STYLE}>
          {categoryLabel}
        </Badge>
        <Badge className={NEUTRAL_STYLE}>For: {audienceLabel}</Badge>
      </div>
      <h3 className="text-base font-semibold text-gray-900">{note.title}</h3>
      <p className="mt-2 text-sm text-gray-700 leading-relaxed whitespace-pre-line">
        {note.summary}
      </p>
    </article>
  );
};

const WhatsNew: React.FC = () => {
  const [audienceFilter, setAudienceFilter] = useState<string>("all");

  const {
    data: notes,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["release-notes", "published"],
    queryFn: getPublishedReleaseNotes,
    staleTime: 5 * 60 * 1000,
  });

  // The audience values actually present, so the filter never offers an empty option.
  const audiences = useMemo(() => {
    const seen = new Set<string>();
    (notes ?? []).forEach((n) => seen.add(n.audience));
    return Array.from(seen).sort();
  }, [notes]);

  const groups = useMemo(() => {
    const filtered =
      audienceFilter === "all"
        ? (notes ?? [])
        : (notes ?? []).filter((n) => n.audience === audienceFilter);
    return groupReleaseNotes(filtered);
  }, [notes, audienceFilter]);

  const total = notes?.length ?? 0;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">What&apos;s New</h1>
        <p className="text-sm text-gray-500 mt-1">
          What has changed in Relevate, newest first — described in terms of
          what it means for your day, not how it was built.
        </p>
      </div>

      {/* Only worth showing once there is more than one audience to choose between. */}
      {audiences.length > 1 && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Show
          </span>
          <button
            type="button"
            onClick={() => setAudienceFilter("all")}
            className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
              audienceFilter === "all"
                ? "bg-primary-600 text-white ring-primary-600"
                : "bg-white text-gray-700 ring-gray-300 hover:bg-gray-50"
            }`}
          >
            Everything
          </button>
          {audiences.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAudienceFilter(a)}
              className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
                audienceFilter === a
                  ? "bg-primary-600 text-white ring-primary-600"
                  : "bg-white text-gray-700 ring-gray-300 hover:bg-gray-50"
              }`}
            >
              {RELEASE_NOTE_AUDIENCE_LABELS[a] ?? humanise(a)}
            </button>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-3 text-sm text-gray-500 py-10">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
          Loading release notes…
        </div>
      )}

      {/* An access refusal and a genuinely empty list are different situations, and reporting
          the first as the second is how a broken read gets mistaken for "nothing shipped". */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          Release notes could not be loaded. If this keeps happening, let the
          team know.
        </div>
      )}

      {!isLoading && !error && total === 0 && (
        <div className="p-6 bg-white border border-gray-200 rounded-lg text-sm text-gray-500">
          There are no release notes published yet.
        </div>
      )}

      {!isLoading && !error && total > 0 && groups.length === 0 && (
        <div className="p-6 bg-white border border-gray-200 rounded-lg text-sm text-gray-500">
          No changes are listed for that audience yet.
        </div>
      )}

      <div className="space-y-8">
        {groups.map((group) => (
          <section key={group.version} data-testid="release-group">
            <div className="flex items-baseline justify-between border-b border-gray-200 pb-2 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {group.published_at
                  ? formatDate(group.published_at)
                  : "Undated"}
              </h2>
              <span className="text-xs font-mono text-gray-400">
                Release {group.version}
              </span>
            </div>
            <div className="space-y-4">
              {group.notes.map((note) => (
                <NoteCard key={note.id} note={note} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default WhatsNew;
